import { Injectable } from "@angular/core";
import { BehaviorSubject, from, Observable, ReplaySubject } from "rxjs";
import { GeneralRequestService } from "./general-request.service";
import { AppMessage } from "../models/app-message.model";
import { AppAttachment } from "../models/attachment.model";
import { FetchMessagesResponseItem } from "../models/payloads/shared/fetch-request.model";
import { MessageStatuses } from "./fetch-classificators.service";
import { differenceInCalendarDays } from "date-fns";

@Injectable({ providedIn: "root" })
export class NotificationService {
  notificationMeetingChanged = new ReplaySubject(1);
  private notificationReply: NotificationReply;
  private isNavigateFromMessage = false;
  private notifications: AppMessage[] = [];
  private allNotifications: AppMessage[] = [];
  private notificationsChanged = new BehaviorSubject<AppMessage[]>(
    this.notifications.slice(),
  );
  codes: {
    patientToHcp: number;
    hcpToPatient: number;
    hcpToHcp: number;
    systemToPatient: number;
  };

  constructor(private generalReqService: GeneralRequestService) {
  }

  fetchNotificationsOnInit() {
    this.setCodes();
    this.generalReqService.fetchAllLatestMessages(100).then((items) => {
      if (items) {
        this.allNotifications = this.transformToAppMessages(items);
        this.notifications = this.filterOutThreadTopAndStatusInProgress(this.allNotifications.slice());
        this.notificationsChanged.next(this.notifications.slice());
      }
    });
  }

  setCodes() {
    this.codes = {
      patientToHcp: JSON.parse(sessionStorage.getItem("MESSAGE_DIRECTION_PATIENT_HCP")),
      hcpToPatient: JSON.parse(sessionStorage.getItem("MESSAGE_DIRECTION_HCP_PATIENT")),
      hcpToHcp: JSON.parse(sessionStorage.getItem("MESSAGE_DIRECTION_HCP_HCP")),
      systemToPatient: JSON.parse(sessionStorage.getItem("MESSAGE_DIRECTION_SYSTEM_PATIENT"))
    };
  }

  fetchNewNotifications(isAutoFetch :boolean = false) {
    this.generalReqService.fetchAllLatestMessages(5, isAutoFetch ).then((items) => {
      if (!items || items.length === 0) return // No new items to process

      // if patient has no messages
      if (this.allNotifications.length === 0) {
        this.allNotifications = this.transformToAppMessages(items);
        this.updateNotifications();
      } else {
        let newItems = this.transformToAppMessages(items);
        let notificationsChanged = false;
        // if new messages arrive
        if (items[0].id !== this.allNotifications[0].messageId) {
          let indexOfCurrentFirst: number;
          notificationsChanged = true;
          for (let i = 0; i < newItems.length; i++) {
            if (newItems[i].messageId === this.allNotifications[0].messageId) {
              indexOfCurrentFirst = i;
              break;
            }
          }
          this.allNotifications = newItems
            .slice(0, indexOfCurrentFirst)
            .concat(this.allNotifications);
        }
        // update message statuses
        newItems.forEach((newItem) => {
          this.allNotifications.forEach((notification, index) => {
            if (newItem.messageId === notification.messageId && newItem.statusCodeId !== notification.statusCodeId) {
              this.allNotifications[index] = newItem;
              notificationsChanged = true;
            }
          });
        });
        // update notifications when necessary
        if (notificationsChanged) {
          this.updateNotifications();
        }
      }
    });
  }

  updateNotifications() {
    this.notifications = this.filterOutThreadTopAndStatusInProgress(this.allNotifications.slice());
    this.notificationsChanged.next(this.notifications.slice());
  }

  fetchMessageThread(messageId: number): Observable<AppMessage> {
    return from(this.generalReqService.fetchMessageThread(messageId)
      .then((items) => {
        if (items && items.length !== 0) {
          return this.transformToAppMessages(items)[0];
        } else return;
      })
    );
  }

  fetchAttachments(id: string): Promise<AppAttachment[]> {
    return this.generalReqService.fetchAttachments(id).then((response) => {
      if (response) {
        let appAttachments: AppAttachment[] = [];
        for (let i = 0; i < response.length; i++) {
          appAttachments.push(
            new AppAttachment(
              response[i].id,
              response[i].metadata,
              response[i].name,
              response[i].messageId,
              response[i].status
            ),
          );
        }
        return appAttachments;
      }
      return;
    });
  }

  getNotifications(): Observable<AppMessage[]> {
    return this.notificationsChanged.asObservable();
  }

  setNotificationReply(reply: NotificationReply) {
    this.notificationReply = reply;
  }

  getNotificationReply() {
    return this.notificationReply;
  }

  setNotificationMeeting(roomCode: string, roomPassword: string) {
    const notificationMeeting: any[] = [];
    notificationMeeting.push(roomCode, roomPassword);
    this.notificationMeetingChanged.next(notificationMeeting.slice());
  }

  setIsNavigateFromMessage(isFromMessage: boolean) {
    this.isNavigateFromMessage = isFromMessage;
  }

  getIsNavigateFromMessage() {
    return this.isNavigateFromMessage;
  }

  readNotification(messageId: number) {
    const notification = this.allNotifications.filter(
      (elem) => elem.messageId === messageId,
    )[0];
    if (notification && notification.isUnread) {
      const statusCodeRead = JSON.parse(
        sessionStorage.getItem("MESSAGE_STATUS_COMPLETED"),
      );
      const statusObj = {
        mode: "SAVE_PARTIAL",
        items: [
          {
            type: "message",
            id: notification.messageId,
            statusCodeId: statusCodeRead,
            crossCommunicationStatus: notification.crossCommunicationStatus,
          },
        ],
      };
      this.generalReqService.postRequest(statusObj);
      const sub = this.generalReqService
        .getRequestResponse()
        .subscribe((body) => {
          sub.unsubscribe();
        });
      notification.isUnread = false;
    }
    this.notifications = this.filterOutThreadTopAndStatusInProgress(this.allNotifications.slice());
    this.notificationsChanged.next(this.notifications.slice());
  }

  private transformToAppMessages(items: FetchMessagesResponseItem[]): AppMessage[] {
    let appMessageArray = [];
    const patientId = JSON.parse(sessionStorage.getItem("sessionContext")).principal.id;

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      const direction = item.directionCodeId;

      const isPatientToHcp = direction === this.codes.patientToHcp && item.sentByPersonId === patientId;
      const isHcpToPatientOrSystemToPatient =
        [this.codes.hcpToPatient, this.codes.systemToPatient].includes(direction) && item.sentToPersonId === patientId;
      const isHcpToHcp = direction === this.codes.hcpToHcp;

      if (isPatientToHcp || isHcpToPatientOrSystemToPatient || isHcpToHcp) {
        let newMessage = this.createAppMessage(item);
        if (item.masterMessage) {
          newMessage.masterMessage = this.transformToAppMessages([item.masterMessage])[0];
        }
        appMessageArray.push(newMessage);
      }
    }
    return appMessageArray;
  }


  private createAppMessage(item: FetchMessagesResponseItem) {
    const statusIsUnread: boolean = item.statusCodeId ? item.statusCodeId === JSON.parse(sessionStorage.getItem("MESSAGE_STATUS_NEW")) : null;
    const messageSender = item.sentByPerson ? item.sentByPerson.firstName.toLowerCase() + " " + item.sentByPerson.lastName.toLowerCase() : null;
    const requireAktCode = +sessionStorage["REQUIRE_TEST_REQUIRE_AKT"];
    const requiresAsthmaTest = !isNaN(requireAktCode) ? item.requiredTestId === requireAktCode : null;
    const masterMessageId = item.masterMessageId ? item.masterMessageId : null;
    const isDirectionToPatient = item.sentToPersonId ? item.sentToPersonId === JSON.parse(sessionStorage.getItem("sessionContext")).principal.id : null;
    const attachmentsExist = item.attachmentsExist || !!(item.attachmentList && item.attachmentList.length > 0 && item.attachmentList[0].id);
    const attachmentList = item.attachmentList ? this.createAppAttachmentList(item.attachmentList) : [];
    const messageSendTimeDate = item.messageSendTime ? new Date(item.messageSendTime) : null;
    const prescriptionStatusId = item.prescriptionStatusId? item.prescriptionStatusId : null;
    let newMessage = new AppMessage(
      item?.typeCode?.code,
      messageSender,
      item.subject,
      new Date(item.insertTime),
      item.directionCodeId,
      statusIsUnread,
      item.content,
      item.id,
      attachmentsExist,
      requiresAsthmaTest,
      item.requiredTestId,
      attachmentList,
      masterMessageId,
      null,
      isDirectionToPatient,
      item.threadTop,
      item.crossCommunicationStatus,
      item.registerId,
      item.sentByPersonId,
      item.sentToPersonId,
      messageSendTimeDate,
      item.statusCodeId,
      item.orderedServiceId,
      item.archived,
      prescriptionStatusId,
    );
    return newMessage;
  }

  /**
   * Filters a list of messages to determine which messages should be displayed based on specific criteria.
   * Messages are included if they are thread tops, have a status of "in progress," or are of type MESSAGE as doctor notifications.
   */
  private filterOutThreadTopAndStatusInProgress(messageList: AppMessage[]): AppMessage[] {
    const inProgressStatus = JSON.parse(sessionStorage.getItem("MESSAGE_STATUS_IN_PROGRESS"));

    return messageList.filter((msg) =>
      msg.threadTop || (!msg.threadTop && msg.statusCodeId === inProgressStatus)
    );
  }

  private createAppAttachmentList(list: any[]): AppAttachment[] {
    let newList = [];
    list.forEach(attachment => {
      newList.push(new AppAttachment(attachment.id, attachment.metadata, attachment.name, attachment.messageId, attachment.status));
    });
    return newList;
  }

  // Returns message state enum
  getMessageState(message: AppMessage, messageStatusTypes: MessageStatuses): MessageState {
    if (!messageStatusTypes) {
      return MessageState.UNKNOWN; // Fallback value if messageStatusTypes is null
    }

    const { NEW, IN_PROGRESS, COMPLETED } = messageStatusTypes;
    const { statusCodeId, directionToPatient } = message;
    const { archived } = message;

    // Doctor has moved the message to archive. Do not show message
    if (statusCodeId === COMPLETED && archived) return MessageState.ARCHIVED;

    // Message from patient to doctor. Doctor has not yet opened the message. Show as "Ootel"
    if (statusCodeId === NEW && !directionToPatient) return MessageState.NEW;

    // Doctor opened the message. Show as "Töös" / Doctor's message is on hold. Show as "Töös"
    if (statusCodeId === IN_PROGRESS && !directionToPatient) return MessageState.IN_PROGRESS;

    // Doctor replied to patient. Show as "Vastatud"
    if (statusCodeId === NEW && directionToPatient && message.masterMessageId) return MessageState.ANSWERED;

    // Doctor initiated the message. Show as "Uus kiri"
    if (statusCodeId === NEW && directionToPatient && !message.masterMessageId) return MessageState.DOCTOR_INITIATED;

    // Patient read the message (hidden from the list)
    if (statusCodeId === COMPLETED && directionToPatient) return MessageState.COMPLETED;

    return MessageState.UNKNOWN;
  }

  setInitialMessageData(message: AppMessage): AppMessage {
    const initialMessage = this.getInitialMessage(message);

    message.initialMessagePrescriptionStatusId = initialMessage.prescriptionStatusId;
    message.initialMessageIsRecent = this.isMessageRecent(initialMessage.date);

    return message;
  }

  isMessageRecent(messageDate: string | Date): boolean {
    const today = new Date();
    const msgDate = new Date(messageDate);
    const daysDiff = differenceInCalendarDays(today, msgDate);
    return daysDiff <= 7;
  }

  getInitialMessage(message: AppMessage): AppMessage {
    // Traverse masterMessage chain until root is found
    let currentMessage = message;
    while (currentMessage.masterMessage) {
      currentMessage = currentMessage.masterMessage;
    }
    return currentMessage;
  }

}

export class NotificationReply {
  message: AppMessage;
  includeAsthmaTest: boolean;

  constructor(message: AppMessage, includeAsthmaTest: boolean) {
    this.message = message;
    this.includeAsthmaTest = includeAsthmaTest;
  }
}

export enum MessageState {
  NEW = "NEW",
  IN_PROGRESS = "IN_PROGRESS",
  ANSWERED = "ANSWERED",
  COMPLETED = "COMPLETED",
  UNKNOWN = "UNKNOWN",
  DOCTOR_INITIATED = "DOCTOR_INITIATED",
  ARCHIVED = "ARCHIVED"
}

export interface MessageWithState extends AppMessage {
  messageState: MessageState;
}
